import { Component, OnInit, Input, Output, EventEmitter, DestroyRef } from '@angular/core';
import * as MenuActions from '@states/menu/menu.actions';
import { MenuKey } from '@enums/menu.enum';
import { select, Store } from '@ngrx/store';
import {
  UntypedFormGroup,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { Observable } from 'rxjs';
import * as SearchConfigurationSelectors from '@states/camera-edit/camera-edit.selectors';
import * as SearchConfigurationActions from '@states/camera-edit/camera-edit.actions';
import { SearchConfigurationEffect } from '@effects/search-configuration.effect';
import { LocationModel } from '../../../../../../../../locations/location.model';
import { EdgeCamera } from '../../../../../../../../cameras/camera.model';
import { AppState } from '../../../../../../../../reducers';
import { NgForOf, NgIf } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MatOption, MatSelect } from '@angular/material/select';
import { UiToggleComponent } from '@ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-configuration-tab',
  templateUrl: './configuration-tab-component-v2.component.html',
  styleUrls: ['./configuration-tab-component-v2.component.scss'],
  imports: [
    ReactiveFormsModule,
    NgIf,
    TranslatePipe,
    MatSelect,
    MatOption,
    UiToggleComponent,
    NgForOf,
  ],
})
export class ConfigurationTabComponentV2 implements OnInit {

  @Input() standalone = false;
  @Output() onConfigurationChange = new EventEmitter<void>();
  public NetworkProfile = LocationModel.NetworkProfile;
  public StreamId = EdgeCamera.StreamId;
  public selectSelectedCamera$: Observable<EdgeCamera.CameraItem> = this.store$.pipe(
    select(SearchConfigurationSelectors.selectSelectedCamera),
  );
  public codecTypes = Object.values(EdgeCamera.CodecType);
  public streamIds = Object.values(EdgeCamera.StreamId);
  public profileForm: UntypedFormGroup;
  public audioConfigurationForm = new FormGroup({
    mainStreamEnabled: new FormControl(true),
    subStreamEnabled: new FormControl(true),
  });
  public configurationForm = new UntypedFormGroup({
    hqConfig: new FormGroup({
      liveKit: new FormGroup({
        isPassthrough: new FormControl(false),
        codec: new FormControl(EdgeCamera.CodecType.H264),
        maxWidth: new FormControl(-2), // -2 means aspect ratio is preserved
        maxHeight: new FormControl(1080),
        fps: new FormControl(15),
        iFrameInterval: new FormControl(32768),
        maxBitrate: new FormControl(3000000),
        minBitrate: new FormControl(800000),
        startBitrate: new FormControl(2100000),
      }),
      isOnlyWhenNeeded: new FormControl(false),
    }),
    mqConfig: new FormGroup({
      liveKit: new FormGroup({
        isPassthrough: new FormControl(false),
        codec: new FormControl(EdgeCamera.CodecType.H264),
        maxWidth: new FormControl(null), // null means don't change width
        maxHeight: new FormControl(null), // null means don't change height
        fps: new FormControl(15),
        iFrameInterval: new FormControl(32768),
        maxBitrate: new FormControl(1000000),
        minBitrate: new FormControl(350000),
        startBitrate: new FormControl(700000),
      }),
      enable: new FormControl(true),
      streamSource: new FormControl(EdgeCamera.StreamId.substream1),
      isOnlyWhenNeeded: new FormControl({ value: null, disabled: true }), // disabled as not supported for MQ
    }),
    sqConfig: new FormGroup({
      liveKit: new FormGroup({
        isPassthrough: new FormControl(false),
        codec: new FormControl(EdgeCamera.CodecType.H264),
        maxWidth: new FormControl(-2), // -2 means aspect ratio is preserved
        maxHeight: new FormControl(240),
        fps: new FormControl(15),
        iFrameInterval: new FormControl(32768),
        maxBitrate: new FormControl(200000),
        minBitrate: new FormControl(10000),
        startBitrate: new FormControl(140000),
      }),
      enable: new FormControl(true),
      streamSource: new FormControl(EdgeCamera.StreamId.substream1),
      isOnlyWhenNeeded: new FormControl({ value: null, disabled: true }), // disabled as not supported for SQ
    }),
    analyticsStreamSource: new FormControl(EdgeCamera.StreamId.mainstream),
    smartStorage: new FormGroup({
      enabled: new FormControl(true),
    }),
    storageStream: new FormGroup({
      storageFromSubstream: new FormControl(false),
      storageSubstreamIndex: new FormControl(0),
    }),
  });
  public updatedConfiguration: any = {};
  public updatedAudioConfiguration: any = {};
  private camera: EdgeCamera.CameraItem;

  constructor(
    private store$: Store<AppState>,
    private editCameraEffects: SearchConfigurationEffect,
    private destroyRef: DestroyRef,
  ) {
  }

  public isProfileSelected(profile: LocationModel.NetworkProfile): boolean {
    return this.profileForm.get('profile').value == profile;
  }

  ngOnInit(): void {
    // Initialize profile form based on standalone mode
    this.profileForm = new UntypedFormGroup({
      profile: new FormControl(this.standalone ? null : LocationModel.NetworkProfile.HighQuality),
    });

    // Enable configuration controls by default in standalone mode (when no profile is selected)
    if (this.standalone) {
      this.enableConfigurationControls();
    }

    // Set active menu only if not in standalone mode
    if (!this.standalone) {
      this.store$.dispatch(
        MenuActions.setActiveLevel2Menu({
          activeLevel2: MenuKey.configuration,
        }),
      );
    }

    // Subscribe to selected camera only if not in standalone mode
    if (!this.standalone) {
      this.selectSelectedCamera$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(camera => {
          this.camera = camera;
          if (camera && camera.edgeOnly) {
            // Patch form with camera configuration fields
            const patchData: any = {};

            if (camera?.edgeOnly?.mainStream?.audioInputEnabled !== undefined) {
              this.audioConfigurationForm.patchValue({
                mainStreamEnabled: camera.edgeOnly.mainStream.audioInputEnabled,
              });
            }

            if (camera?.edgeOnly?.subStreams?.[0]?.audioInputEnabled !== undefined) {
              this.audioConfigurationForm.patchValue({
                subStreamEnabled: camera.edgeOnly.subStreams[0].audioInputEnabled,
              });
            }

            if (camera.edgeOnly.hqConfig) {
              patchData.hqConfig = camera.edgeOnly.hqConfig;
            }

            if (camera.edgeOnly.mqConfig) {
              patchData.mqConfig = camera.edgeOnly.mqConfig;
            }

            if (camera.edgeOnly.sqConfig) {
              patchData.sqConfig = camera.edgeOnly.sqConfig;
            }

            if (camera.edgeOnly.analyticsStreamSource) {
              patchData.analyticsStreamSource = camera.edgeOnly.analyticsStreamSource;
            }

            if (camera.edgeOnly.smartStorage) {
              patchData.smartStorage = camera.edgeOnly.smartStorage;
            }

            if (camera.edgeOnly.storageStream) {
              patchData.storageStream = camera.edgeOnly.storageStream;
            }

            this.configurationForm.patchValue(patchData);

            // Patch profile form from camera network profile, default to HighQuality if not defined
            const networkProfile = camera.edgeOnly.networkProfile || LocationModel.NetworkProfile.HighQuality;
            this.profileForm.patchValue({ profile: networkProfile });

            // Enable/disable form controls based on the loaded profile
            this.applyProfileControlState(networkProfile);

            // Apply passthrough state after loading data
            setTimeout(() => this.applyPassthroughState(), 0);
          }
        });
    }

    // Track configuration form changes
    this.configurationForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      const currentProfile = this.profileForm.get('profile')?.value;

      // For preset profiles, preserve the complete preset configuration
      // Only update for custom profile or when no profile is selected (null) to allow manual editing
      if (currentProfile === LocationModel.NetworkProfile.Custom || currentProfile === null) {
        // In standalone mode, only include fields the user actually changed
        const values = this.standalone
          ? this.getDirtyValues(this.configurationForm)
          : res;

        this.updatedConfiguration = {
          ...values,
          networkProfile: currentProfile,
        };

        // Emit change event for standalone mode
        if (this.standalone) {
          this.onConfigurationChange.emit();
        }
      }
    });

    // Track profile form changes
    this.profileForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.updatedConfiguration = {
        ...this.updatedConfiguration,
        networkProfile: res.profile, // Include networkProfile (can be null for no profile selected)
      };

      // Emit change event for standalone mode
      if (this.standalone) {
        this.onConfigurationChange.emit();
      }
    });

    // Monitor passthrough changes for HQ config
    this.configurationForm.get('hqConfig.liveKit.isPassthrough')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isPassthrough => {
        this.toggleLiveKitControls('hqConfig', isPassthrough);
      });

    // Monitor passthrough changes for MQ config
    this.configurationForm.get('mqConfig.liveKit.isPassthrough')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isPassthrough => {
        this.toggleLiveKitControls('mqConfig', isPassthrough);
      });

    // Monitor passthrough changes for SQ config
    this.configurationForm.get('sqConfig.liveKit.isPassthrough')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isPassthrough => {
        this.toggleLiveKitControls('sqConfig', isPassthrough);
      });

    // Subscribe to save action only if not in standalone mode
    if (!this.standalone) {
      this.editCameraEffects.pressSave$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        const currentProfile = this.profileForm.get('profile')?.value;
        let updateData: any;

        // Handle different profiles differently for validation and data preparation
        switch (currentProfile) {
          case LocationModel.NetworkProfile.HighQuality:
            // Use preset high quality configuration - no validation needed
            updateData = {
              ...this.getHighQualityConfiguration(),
              networkProfile: currentProfile,
            };
            break;

          case LocationModel.NetworkProfile.LowBandwidth:
            // Use preset low bandwidth configuration - no validation needed
            updateData = {
              ...this.getLowBandwidthConfiguration(),
              networkProfile: currentProfile,
            };
            break;

          case LocationModel.NetworkProfile.Custom:
            // Validate form for custom configuration
            if (this.configurationForm.valid && this.profileForm.valid) {
              updateData = {
                ...this.updatedConfiguration,
                networkProfile: currentProfile,
              };
            } else {
              console.warn('Configuration form is invalid, cannot save custom profile');
              return; // Exit early if validation fails for custom profile
            }
            break;

          default:
            console.warn(`Unknown network profile: ${currentProfile}, skipping save`);
            return;
        }

        // update audio configuration
        const audioConfig = this.audioConfigurationForm.value;
        updateData = {
          ...updateData,
          mainStream: {
            audioInputEnabled: audioConfig.mainStreamEnabled,
          },

        };
        if (this.camera.edgeOnly?.subStreams?.length > 0) {
          updateData = {
            ...updateData, subStreams: [{
              ...this.camera.edgeOnly.subStreams[0], audioInputEnabled: audioConfig.subStreamEnabled,
            }],
          };
        }

        // Dispatch the update action
        this.store$.dispatch(
          SearchConfigurationActions.cameraUpdate({
            data: updateData,
          }),
        );
      });
    }

    this.audioConfigurationForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(audioConfig => {
      if (audioConfig.mainStreamEnabled !== null && audioConfig.mainStreamEnabled !== undefined) {
        if (!audioConfig.mainStreamEnabled) {
          this.updatedAudioConfiguration.mainStreamEnabled = audioConfig.mainStreamEnabled;
        } else {
          if (audioConfig.mainStreamEnabled) {
            delete this.updatedAudioConfiguration.mainStreamEnabled;
          }
        }
      }
      if (audioConfig.subStreamEnabled !== null && audioConfig.subStreamEnabled !== undefined) {
        if (!audioConfig.subStreamEnabled) {
          this.updatedAudioConfiguration.subStreamEnabled = audioConfig.subStreamEnabled;
        } else {
          if (audioConfig.subStreamEnabled) {
            delete this.updatedAudioConfiguration.subStreamEnabled;
          }
        }
      }

      // Emit change event for standalone mode
      if (this.standalone) {
        this.onConfigurationChange.emit();
      }
    });

  }

  /**
   * Select a network profile and apply corresponding configuration
   * @param profile - The network profile to select (HighQuality, LowBandwidth, or Custom)
   */
  public selectProfile(profile: LocationModel.NetworkProfile): void {
    this.profileForm.get('profile').setValue(profile);

    // Apply profile-specific configurations and control states
    switch (profile) {
      case LocationModel.NetworkProfile.HighQuality:
        this.applyHighQualityConfiguration();
        this.disableConfigurationControls();
        // Emit change event after configuration is applied
        this.emitStandaloneChange();
        break;

      case LocationModel.NetworkProfile.LowBandwidth:
        this.applyLowBandwidthConfiguration();
        this.disableConfigurationControls();

        // Emit change event after configuration is applied
        this.emitStandaloneChange();
        break;

      case LocationModel.NetworkProfile.Custom:
        this.enableConfigurationControls();
        // Emit change event immediately for custom profile
        if (this.standalone) {
          this.onConfigurationChange.emit();
        }
        break;

      default:
        console.warn(`Unknown network profile: ${profile}`);
        break;
    }
  }

  /**
   * Apply High Quality network profile configuration
   */
  private applyHighQualityConfiguration(): void {
    const config = this.getHighQualityConfiguration();
    this.configurationForm.patchValue(config);

    // Update the updatedConfiguration object for standalone mode
    this.updatedConfiguration = {
      ...config,
      networkProfile: this.profileForm.get('profile')?.value,
    };

    // Apply passthrough state after setting values
    setTimeout(() => this.applyPassthroughState(), 0);
  }

  /**
   * Apply Low Bandwidth network profile configuration
   */
  private applyLowBandwidthConfiguration(): void {
    const config = this.getLowBandwidthConfiguration();
    this.configurationForm.patchValue(config);

    // Update the updatedConfiguration object for standalone mode
    this.updatedConfiguration = {
      ...config,
      networkProfile: this.profileForm.get('profile')?.value,
    };

    // Apply passthrough state after setting values
    setTimeout(() => this.applyPassthroughState(), 0);
  }

  /**
   * Disable all configuration form controls to prevent manual editing
   */
  private disableConfigurationControls(): void {
    this.configurationForm.disable();
    // Apply passthrough state after disabling to ensure correct visual state
    setTimeout(() => this.applyPassthroughState(), 0);
  }

  /**
   * Enable all configuration form controls to allow manual editing
   */
  private enableConfigurationControls(): void {
    this.configurationForm.enable();

    // Keep specific controls disabled that are not supported
    this.configurationForm.get('mqConfig.isOnlyWhenNeeded')?.disable();
    this.configurationForm.get('sqConfig.isOnlyWhenNeeded')?.disable();

    // Apply passthrough state for each config section
    this.applyPassthroughState();
  }

  /**
   * Toggle LiveKit controls based on passthrough state
   * @param configSection - The configuration section (hqConfig, mqConfig, or sqConfig)
   * @param isPassthrough - Whether passthrough is enabled
   */
  private toggleLiveKitControls(configSection: string, isPassthrough: boolean): void {
    const profile = this.profileForm.get('profile')?.value;
    const disable = isPassthrough || profile === LocationModel.NetworkProfile.HighQuality || profile === LocationModel.NetworkProfile.LowBandwidth;
    const liveKitControls = [
      'codec', 'maxWidth', 'maxHeight', 'fps', 'iFrameInterval',
      'maxBitrate', 'minBitrate', 'startBitrate',
    ];

    liveKitControls.forEach(controlName => {
      const control = this.configurationForm.get(`${configSection}.liveKit.${controlName}`);
      if (control) {
        if (disable) {
          control.disable();
        } else {
          control.enable();
        }
      }
    });
  }

  /**
   * Apply passthrough state to all configuration sections
   */
  private applyPassthroughState(): void {
    const sections = ['hqConfig', 'mqConfig', 'sqConfig'];

    sections.forEach(section => {
      const isPassthrough = this.configurationForm.get(`${section}.liveKit.isPassthrough`)?.value;
      if (typeof isPassthrough === 'boolean') {
        this.toggleLiveKitControls(section, isPassthrough);
      }
    });
  }

  /**
   * Emit configuration change event for standalone mode after a delay
   * This ensures all async operations (like passthrough state) are complete
   */
  private emitStandaloneChange(): void {
    if (this.standalone) {
      // Use a longer delay to ensure all form updates and passthrough state changes are complete
      setTimeout(() => {
        this.onConfigurationChange.emit();
      }, 10);
    }
  }

  /**
   * Apply profile control state without changing configuration values
   * Used when loading existing camera configuration to set proper control states
   * @param profile - The network profile to apply control state for
   */
  private applyProfileControlState(profile: LocationModel.NetworkProfile): void {
    switch (profile) {
      case LocationModel.NetworkProfile.HighQuality:
      case LocationModel.NetworkProfile.LowBandwidth:
        this.disableConfigurationControls();
        break;

      case LocationModel.NetworkProfile.Custom:
        this.enableConfigurationControls();
        break;

      default:
        console.warn(`Unknown network profile: ${profile}, defaulting to Custom control state`);
        this.enableConfigurationControls();
        break;
    }
  }

  private getDirtyValues(group: UntypedFormGroup | FormGroup): Record<string, any> {
    const result: Record<string, any> = {};
    for (const key of Object.keys(group.controls)) {
      const control = group.controls[key];
      if (control instanceof FormGroup || control instanceof UntypedFormGroup) {
        const nested = this.getDirtyValues(control);
        if (Object.keys(nested).length > 0) {
          result[key] = nested;
        }
      } else if (control.dirty) {
        result[key] = control.value;
      }
    }
    return result;
  }

  /**
   * Get the High Quality network profile configuration
   */
  private getHighQualityConfiguration(): any {
    return {
      analyticsStreamSource: EdgeCamera.StreamId.mainstream,
      hqConfig: {
        isOnlyWhenNeeded: false,
        liveKit: {
          isPassthrough: true,
          codec: EdgeCamera.CodecType.H264, // today H264 is the only option
          maxWidth: -2, // this means that the aspect ratio is preserved
          maxHeight: 1080,
          fps: 15,
          iFrameInterval: 32768,
          maxBitrate: 3000000,
          minBitrate: 800000,
          startBitrate: 2100000,
        },
      },
      mqConfig: {
        isOnlyWhenNeeded: undefined, // this field is not supported for MQ (maybe in the future)
        liveKit: {
          isPassthrough: false,
          codec: EdgeCamera.CodecType.H264, // today H264 is the only option
          maxWidth: null, // by default we don't change the width
          maxHeight: null, // by default we don't change the height
          fps: 15,
          iFrameInterval: 32768,
          maxBitrate: 1000000,
          minBitrate: 350000,
          startBitrate: 700000,
        },
      },
      sqConfig: {
        isOnlyWhenNeeded: undefined, // this field is not supported for SQ (maybe in the future)
        liveKit: {
          isPassthrough: false,
          codec: EdgeCamera.CodecType.H264, // today H264 is the only option
          maxWidth: -2, // this means that the aspect ratio is preserved
          maxHeight: 240,
          fps: 15,
          iFrameInterval: 32768,
          maxBitrate: 200000,
          minBitrate: 10000,
          startBitrate: 140000,
        },
      },
      smartStorage: {
        enabled: true,
      },
    };
  }

  /**
   * Get the Low Bandwidth network profile configuration
   */
  private getLowBandwidthConfiguration(): any {
    return {
      analyticsStreamSource: EdgeCamera.StreamId.substream1, // Use substream for analytics
      smartStorage: {
        enabled: false,
      },
      storageStream: {
        storageFromSubstream: true,
        storageSubstreamIndex: 0,
      },
      hqConfig: {
        isOnlyWhenNeeded: true,
        liveKit: {
          // Same as normal/high quality for hqConfig liveKit settings
          isPassthrough: true,
          codec: EdgeCamera.CodecType.H264,
          maxWidth: -2,
          maxHeight: 1080,
          fps: 15,
          iFrameInterval: 32768,
          maxBitrate: 3000000,
          minBitrate: 800000,
          startBitrate: 2100000,
        },
      },
      mqConfig: {
        // Same as normal/high quality for mqConfig
        isOnlyWhenNeeded: undefined,
        liveKit: {
          isPassthrough: false,
          codec: EdgeCamera.CodecType.H264,
          maxWidth: null,
          maxHeight: null,
          fps: 15,
          iFrameInterval: 32768,
          maxBitrate: 1000000,
          minBitrate: 350000,
          startBitrate: 700000,
        },
      },
      sqConfig: {
        // Same as normal/high quality for sqConfig
        isOnlyWhenNeeded: undefined,
        liveKit: {
          isPassthrough: false,
          codec: EdgeCamera.CodecType.H264,
          maxWidth: -2,
          maxHeight: 240,
          fps: 15,
          iFrameInterval: 32768,
          maxBitrate: 200000,
          minBitrate: 10000,
          startBitrate: 140000,
        },
      },
    };
  }
} 